import C from "@/admin/AdminTable.module.css";

import { AdminScrollPane } from "@/admin/AdminLayout.tsx";
import { AdminLocationMenuContent } from "@/admin/AdminLocationSelect.tsx";
import { AdminPowerTable, type AdminPowerTableProps } from "@/admin/AdminTable.tsx";
import { AdminTitle } from "@/admin/AdminTitle.tsx";
import type { GqFieldFilter, GqlFieldConfig, GqlFieldsOf } from "@/admin/FieldConfig.tsx";
import { FilterContext } from "@/admin/FilterContext.ts";
import {
    deserializeQuickFilter,
    isEmptyFilter,
    SerializedQuickFilterSchema,
    serializeQuickFilter,
} from "@/admin/filterUtils.ts";
import { OrderDirection } from "@/admin/graphql.gen/graphql.ts";
import type { GqData } from "@/admin/graphql/extractData.tsx";
import {
    type CursorVariables,
    type PageVariables,
    type PagingResult,
    usePagePager,
    type UsePagerFn,
} from "@/admin/pager.ts";
import { Pager } from "@/admin/Pager.tsx";
import { XSticky } from "@/admin/StickyArea.tsx";
import { locationField } from "@/admin/table_fields/standardFields.tsx";
import { useZodSearchParams } from "@/admin/utils/useZodParse.tsx";
import { type Action, link } from "@/components/Action.tsx";
import { ActionMenu, type ActionMenuItemProps } from "@/components/ActionMenu.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { WTooltipButton } from "@/components/button/WToolButton.tsx";
import { WSearchField } from "@/components/forms/WSearchField.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { ContentLoadingSuspense } from "@/components/loading/Loading.tsx";
import { locationSlugsAtom } from "@/modules/api/locations.store.ts";
import { ErrorRow, WErrorBoundary } from "@/modules/error/ErrorFallback.tsx";
import { usePricingLocations } from "@/modules/location/locationQuery.ts";
import { Resizable, type ResizerSettings } from "@/modules/main/sidebar/Sidebar.tsx";
import { cn } from "@/utils/classNames.ts";
import { nothing, produce } from "@/utils/immer.ts";
import { deepEqual, useNavigate } from "@tanstack/react-router";
import { emptyToUndefined } from "@warrenio/utils/collections/emptyToUndefined";
import { filterNulls } from "@warrenio/utils/collections/filterNulls";
import { getOwn } from "@warrenio/utils/collections/getOwnProperty";
import { notNull } from "@warrenio/utils/notNull";
import { discardPromise } from "@warrenio/utils/promise/discardPromise";
import { useAtomValue } from "jotai/react";
import { Fragment, type ReactNode, type Ref, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { isEmpty, keys } from "remeda";
import type { DraftFunction } from "use-immer";
import z from "zod";

function toggleOrder(orderDir: OrderDirection | undefined): OrderDirection {
    return orderDir !== OrderDirection.Desc ? OrderDirection.Desc : OrderDirection.Asc;
}

/** Map from field ID to whether it is visible */
type VisibleOverrideState = Record<string, boolean>;

function ColumnVisibilityMenu({
    visibleFieldIds,
    visibleOverride,
    modifyVisibleOverride,
    fields,
}: {
    visibleFieldIds: string[];
    /** Subset of {@link GqlFieldConfig} */
    fields: { id: string; title: ReactNode; hidden?: boolean }[];

    visibleOverride: VisibleOverrideState;
    modifyVisibleOverride: (apply: DraftFunction<VisibleOverrideState>) => void;
}) {
    const hasOverrides = !isEmpty(visibleOverride);
    return (
        <ActionMenu
            header={
                <>
                    <div>Columns</div>
                    <div className="ml-auto">
                        <WTooltipButton
                            title="Reset to default"
                            isDisabled={!hasOverrides}
                            color="muted"
                            variant="ghost"
                            size="xs"
                            icon="i-lucide:list-restart"
                            action={() =>
                                modifyVisibleOverride(() => {
                                    for (const key of keys(visibleOverride)) {
                                        delete visibleOverride[key];
                                    }
                                })
                            }
                        />
                    </div>
                </>
            }
            items={fields.map(
                (f): ActionMenuItemProps => ({
                    id: f.id,
                    title: f.title,
                    action: () =>
                        modifyVisibleOverride((visibleOverride) => {
                            const base = !f.hidden;
                            const current = getOwn(visibleOverride, f.id) ?? base;
                            const target = !current;
                            // Only write an override if the desired value is different from the default
                            if (base !== target) {
                                visibleOverride[f.id] = target;
                            } else {
                                delete visibleOverride[f.id];
                            }
                        }),
                }),
            )}
            selectedKeys={visibleFieldIds}
            selectionMode="multiple"
        >
            <WTooltipButton
                title="Column visibility"
                color={hasOverrides ? "primary" : undefined}
                variant="ghost"
                icon="jp-icon-toggles"
                action={undefined}
            />
        </ActionMenu>
    );
}

type LocationsState = string[] | null | undefined;

/**
 * - Key: Arbitrary ID, currently field ID
 * - Value: `null` when filter has not been initialized yet
 */
type QuickFilterState<TOrderFields extends string = string> = GqFieldFilter<TOrderFields>[];

const FlexBorder = () => <div className={C.FlexBorder} />;

function FilterItem({ title, children, onClose }: { title: ReactNode; children: ReactNode; onClose?: Action }) {
    return (
        <div className="flex flex-row items-center gap-2 py-2 pl-1">
            {title}
            {children}
            <WButton variant="ghost" size="xs" icon="jp-icon-close" action={onClose} />
        </div>
    );
}

function QuickFilterEditor<TOrderFields extends string>({
    filters,
    topRef,
    fields,
    modifyFilters,
    locations,
    setLocations,
}: {
    fields: GqlFieldsOf<any, TOrderFields>;

    filters: QuickFilterState<TOrderFields>;
    modifyFilters: (apply: (draft: QuickFilterState<TOrderFields>) => void) => void;

    locations: LocationsState;
    setLocations: (locations: LocationsState) => void;

    /** Ref to the top of the this element, can be used for scrolling */
    topRef: Ref<HTMLDivElement>;
}) {
    const defaultLocations = usePricingLocations();

    const hasLocationFilter = locations !== undefined;
    const hasFilters = !isEmpty(filters) || hasLocationFilter;

    return (
        <div className={cn(!hasFilters && "hidden", C.FilterBorder, "HStack")} ref={topRef}>
            <div className="p-2 text-muted">Filters:</div>
            <FlexBorder />
            {filters.map((filterParams, idx) => {
                const field = filterParams.field;
                // XXX: This does not work if multiple table fields filter on the same GraphQL field
                const fieldConfig = fields.find((f) => f.order === field);
                if (!fieldConfig) {
                    console.warn("Unknown filter field:", filterParams);
                    return null;
                }

                const FilterComponent = notNull(fieldConfig.filter);

                return (
                    <Fragment key={field}>
                        <FilterItem
                            title={fieldConfig.title}
                            onClose={() =>
                                modifyFilters((s) => {
                                    s.splice(idx, 1);
                                })
                            }
                        >
                            <FilterContext.Provider
                                value={{
                                    value: filterParams,
                                    setValue: (value) =>
                                        modifyFilters((s) => {
                                            s[idx] = { field, ...value };
                                        }),
                                    fieldConfig,
                                }}
                            >
                                <FilterComponent />
                            </FilterContext.Provider>
                        </FilterItem>
                        <FlexBorder />
                    </Fragment>
                );
            })}
            {hasLocationFilter && (
                <>
                    <FilterItem title="Locations" onClose={() => setLocations(undefined)}>
                        <AdminLocationMenuContent
                            value={locations ?? []}
                            locations={defaultLocations}
                            deletedLocations={[]}
                            onChange={(locations) => {
                                setLocations(locations);
                            }}
                            canUnselectAll={false}
                        />
                    </FilterItem>
                    <FlexBorder />
                </>
            )}
        </div>
    );
}

/** Common variables passed to all GraphQL table queries (used for eg. sorting and filtering) */
export interface StandardVariables<TOrderFields extends string>
    extends Partial<PageVariables>, Partial<CursorVariables> {
    orderField?: TOrderFields;
    orderDir?: OrderDirection;
    /** Arbitrary free-text query to search for */
    search?: string;
    filters?: GqFieldFilter<TOrderFields>[];
    /** NB: This field is not always used. Apollo just ignores this if it is not specified in the query params. */
    locations?: string[];
}

interface TableViewState<TOrderFields extends string = string> {
    orderDir: OrderDirection;
    orderField?: TOrderFields;
    search: string;
    visibleOverride: VisibleOverrideState;
    detailId?: string;
    locations?: LocationsState;
}

const TableUrlParamsSchema = z
    .object({
        orderDir: z.nativeEnum(OrderDirection),
        orderField: z.string().optional(),
        search: z.string(),
        filters: z.array(SerializedQuickFilterSchema),
        visibleOverride: z.record(z.boolean()),
        detailId: z.string().optional(),
        pg: z.unknown().optional().describe("Pager state"),
        locations: z.array(z.string()).nullable().optional(),
    })
    .partial()
    .passthrough();

type TableUrlParams = z.infer<typeof TableUrlParamsSchema>;

function useQuickFilterState(
    urlFilters: TableUrlParams["filters"],
    setUrlFilters: (value: TableUrlParams["filters"]) => void,
) {
    // Store the state we write into the URL so we can avoid double updates
    // (since we also update the state when the URL changes)
    const currentUrlFilters = useRef<TableUrlParams["filters"]>(undefined);

    const deserializeFilters = useCallback(() => urlFilters?.map(deserializeQuickFilter) ?? [], [urlFilters]);

    // Keep quick filter state separately since it might contain "extra" state that we do not want to put in the URL
    const [quickFilters, setQuickFilters] = useState<QuickFilterState>(deserializeFilters);

    // Update quick filter state when URL state changes
    useEffect(() => {
        // Skip our own updates
        if (!deepEqual(urlFilters, currentUrlFilters.current)) {
            currentUrlFilters.current = urlFilters;

            console.info("Updating quick filters from URL: %o", urlFilters);
            // eslint-disable-next-line react-hooks/set-state-in-effect -- Should use `onFiltersChange` event or something, but that's too much refactoring for now
            setQuickFilters(deserializeFilters());
        }
    }, [urlFilters, deserializeFilters]);

    const updateQuickFilters = (fn: (draft: QuickFilterState) => void) => {
        const newValue = produce(quickFilters, fn);
        setQuickFilters(newValue);

        // Drop any "half-complete" filters
        const newUrlValue = emptyToUndefined(filterNulls(newValue.map(serializeQuickFilter)));

        // Update URL with new state
        setUrlFilters(newUrlValue);
        currentUrlFilters.current = newUrlValue;
    };

    return [quickFilters, updateQuickFilters] as const;
}

const defaultState: TableViewState = {
    orderDir: OrderDirection.Desc,
    orderField: undefined,
    search: "",
    visibleOverride: {},
};

/** Standard GraphQL structure for pageable results */
export type GqItems<TItem> = GqData<{ items?: TItem[] | null; paging?: PagingResult }>;

export function GraphqlTable<TItem, TOrderFields extends string>({
    title,
    fields,
    hasLocations = true,
    useQuery,
    defaults,
    getId,
    renderDetailToolbar,
    renderDetail,
    usePager = usePagePager,
    getRowClassname,
    ...props
}: {
    fields: GqlFieldsOf<TItem, TOrderFields>;
    defaults?: Partial<TableViewState<TOrderFields>>;
    hasLocations?: boolean;

    useQuery: (vars: StandardVariables<TOrderFields>) => GqItems<TItem>;
    usePager?: UsePagerFn;

    title: string;
    renderDetail?: (item: TItem) => ReactNode;
    renderDetailToolbar?: (item: TItem) => ReactNode;
} & Omit<AdminPowerTableProps<TItem>, "renderTitle" | "items" | "fields" | "topRef" | "sticky">) {
    //#region Hooks

    const navigate = useNavigate();

    // References to anchor elements (for eg. automatic scrolling)
    const topRef = useRef<HTMLTableSectionElement>(null);
    const filtersTopRef = useRef<HTMLDivElement>(null);

    //#region View state

    const urlState: TableUrlParams | undefined = useZodSearchParams(TableUrlParamsSchema);

    const [quickFilters, setQuickFilters] = useQuickFilterState(urlState?.filters, (value) => {
        setUrlState((draft) => {
            draft.filters = value;
        });
    });

    // NB: Use `string` as the state's `TOrderFields` parameter since otherwise Immer has problems with type errors
    // (drafts are invariant so it's impossible to use `TOrderFields` directly since it occurs in both contravariant and covariant positions below)

    const s: TableViewState = { ...defaultState, ...defaults, ...urlState };
    const isStateEdited = urlState && !isEmpty(urlState);

    const setUrlState = (modify: DraftFunction<TableUrlParams>) => {
        const newValue = produce(urlState, modify);

        // Update URL with new state
        discardPromise(
            navigate({
                // Need a type cast since we do not use TanStack Router strongly typed routes
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                search: newValue != null ? (newValue as any) : ({} as any),
                replace: true,
            }),
        );
    };

    //#endregion

    //#region Data fetching

    const pager = usePager({
        urlState: [
            urlState?.pg,
            (action) => {
                setUrlState((draft) => {
                    draft.pg = action(draft.pg);
                });
            },
        ],
    });

    const setSearchString = (search: string) => {
        // Go to first page when search changes
        pager.goFirst();
        setUrlState((draft) => {
            draft.search = search;
        });
    };

    const allLocations = useAtomValue(locationSlugsAtom);

    const locations = hasLocations ? (s.locations ?? allLocations) : undefined;

    const query = useQuery({
        ...pager.variables,
        // Casts are required due to having `string` instead of `TOrderFields` in the state
        ...(s.orderField ? { orderField: s.orderField as TOrderFields, orderDir: s.orderDir } : undefined),
        locations,
        search: s.search || undefined,
        filters: quickFilters.filter((f) => !isEmptyFilter(f)) as StandardVariables<TOrderFields>["filters"],
    });
    const { data, error, refetch } = query;

    //#endregion

    //#region Detail item

    const { detailId } = s;

    const detailItemRef = useRef<TItem | undefined>(undefined);
    const detailItemFromResults = data?.items?.find((item) => getId(item) === detailId);
    if (detailItemFromResults) {
        detailItemRef.current = detailItemFromResults;
    } else {
        // If we can not find the detail item right now, keep the previous one selected
    }

    const detailItem = detailId != null ? detailItemRef.current : undefined;

    const openDetail = (item: TItem): void => {
        setUrlState((s) => {
            s.detailId = getId(item);
        });

        // Automatically close the side menu when opening the detail view
        /* if (!hasClosedMenu.current) {
            setMenuCompact(true);
            hasClosedMenu.current = true;
        } */
    };

    const closeDetail = () => {
        setUrlState((s) => {
            delete s.detailId;
        });
        detailItemRef.current = undefined;
    };

    const rowLink = // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        (item: TItem) => link({ search: { ...urlState, detailId: getId(item) } } as any);

    //#endregion Detail item

    const visibleFields = useMemo(
        () => fields.filter((f) => getOwn(s.visibleOverride, f.id) ?? !f.hidden),
        [s.visibleOverride, fields],
    );

    //#endregion Hooks

    const menu = (
        <XSticky>
            <AdminTitle title={title}>
                <WSearchField width="flex-grow ml-auto max-w-20em" value={s.search} onChange={setSearchString} />
                {!!refetch && (
                    <WTooltipButton
                        title="Refresh"
                        variant="ghost"
                        icon="i-lucide:refresh-cw"
                        action={async () => {
                            await refetch();
                        }}
                    />
                )}
                <WTooltipButton
                    title="Reset"
                    variant="ghost"
                    isDisabled={!isStateEdited}
                    icon="i-lucide:circle-slash-2"
                    action={() => setUrlState(() => nothing)}
                />
                <ColumnVisibilityMenu
                    fields={fields}
                    visibleFieldIds={visibleFields.map((f) => f.id)}
                    visibleOverride={s.visibleOverride}
                    modifyVisibleOverride={(fn) =>
                        setUrlState((s) => {
                            s.visibleOverride ??= {};
                            fn(s.visibleOverride);
                            if (isEmpty(s.visibleOverride)) {
                                delete s.visibleOverride;
                            }
                        })
                    }
                />
            </AdminTitle>

            <QuickFilterEditor
                topRef={filtersTopRef}
                fields={fields}
                filters={quickFilters}
                modifyFilters={setQuickFilters}
                locations={s.locations}
                setLocations={(locations) => {
                    setUrlState((s) => {
                        s.locations = locations?.length ? locations : undefined;
                    });
                }}
            />
        </XSticky>
    );

    const table = (
        <AdminPowerTable
            sticky
            topRef={topRef}
            getId={getId}
            getRowClassname={(item) => cn(getId(item) === detailId ? C.Selected : undefined, getRowClassname?.(item))}
            fields={visibleFields}
            items={data?.items}
            renderTitle={({ id, order, title, filter }) => {
                const hasFilter = filter && order;

                const isOrdering = order != null && s.orderField === order;
                const isFiltered = order != null && !!quickFilters.find((f) => f.field === order);

                const clickFilter = hasFilter
                    ? () => {
                          // NB: We must fully render the filters element before we scroll to it, otherwise the final scroll position will be wrong (since the height of the page changes during scroll)
                          // https://julesblom.com/writing/flushsync
                          flushSync(() =>
                              setQuickFilters((s) => {
                                  const filterIdx = s.findIndex((f) => f.field === order);
                                  if (filterIdx !== -1) {
                                      // Remove the filter
                                      s.splice(filterIdx, 1);
                                  } else {
                                      s.push({ field: order });
                                  }
                              }),
                          );
                          filtersTopRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
                      }
                    : // Hack for location field
                      id === locationField.id && hasLocations
                      ? () => {
                            setUrlState((s) => {
                                s.locations = null;
                            });
                        }
                      : undefined;

                const clickOrder = order
                    ? () =>
                          setUrlState((s) => {
                              s.orderField = order;
                              if (s.orderField === order) {
                                  s.orderDir = toggleOrder(s.orderDir);
                              }
                          })
                    : undefined;

                return (
                    <div className={cn(order && "cursor-pointer", "HStack gap-1")} onClick={clickOrder}>
                        {isOrdering && (
                            <MaskIcon
                                className={`color-muted ${s.orderDir === OrderDirection.Asc ? "jp-icon-caretup" : "jp-icon-caretdown"} size-0.75rem`}
                            />
                        )}
                        {title}
                        {clickFilter && (
                            <WButton
                                variant="ghost"
                                size="xs"
                                color={isFiltered ? "primary" : "muted"}
                                icon="jp-icon-filter-list"
                                action={clickFilter}
                            />
                        )}
                    </div>
                );
            }}
            onClickRow={renderDetail ? openDetail : undefined}
            rowLink={renderDetail ? rowLink : undefined}
            {...props}
        />
    );

    const footer = (
        <XSticky>
            <Pager className="p-2" pager={pager} topRef={topRef} result={data?.paging} />
        </XSticky>
    );

    return (
        <div className="flex flex-row size-full">
            <AdminScrollPane>
                {menu}
                {error && (
                    <XSticky>
                        <ErrorRow error={error} tags="GraphqlTable" />
                    </XSticky>
                )}
                {table}
                {footer}
            </AdminScrollPane>
            {detailItem != null && renderDetail && (
                <Resizable className="overflow-x-auto overflow-y-scroll contain-strict" settings={resizerSettings}>
                    {/* Force remount on row switch so nested detail blocks reset item-specific local state (eg. VM Domain XML). */}
                    <WErrorBoundary key={getId(detailItem)} tags="GraphqlTable_detail">
                        <div className={cn(C.Toolbar, "HStack")}>
                            <Suspense>{renderDetailToolbar?.(detailItem)}</Suspense>

                            <div className={C.ToolbarClose}>
                                <WButton
                                    size="bar"
                                    variant="ghost"
                                    icon="jp-icon-close"
                                    ariaLabel="Close"
                                    action={closeDetail}
                                />
                            </div>
                        </div>

                        <ContentLoadingSuspense>{renderDetail(detailItem)}</ContentLoadingSuspense>
                    </WErrorBoundary>
                </Resizable>
            )}
        </div>
    );
}

const resizerSettings: ResizerSettings = {
    minWidth: 300,
    defaultWidth: 460,
    maxWidth: Infinity,
    maxScreenRatio: 0.8,

    storageKey: "adminTableDetailsWidth",
    handleSide: "left",
};
